// ═══════════════════════════════════════════════════════════════
// report-error — one place every unexpected failure goes.
//
// Before this, every failure in the app ended at console.error on a
// phone nobody will ever look at. When a paying mechanic says "it
// broke," that has to leave a trace somewhere readable.
//
// Deliberately NOT a third-party SDK yet: no new vendor account, no
// new dependency, no bundle weight.
//
// Reports go to the top-level /errorReports collection rather than
// users/{uid}/errors, for the same reason feedback does: a crash
// only the crashing user can see is a crash nobody acts on. The
// rules let any signed-in user file one stamped with their own uid,
// and only an admin read them back.
//
// KNOWN GAP: the public estimate and invoice pages are viewed by
// customers who are not signed in, so a crash there still can't be
// recorded — the alternative is an unauthenticated write path into a
// shared collection, which is a spam target. The boundary still shows
// the customer something sane; the report just doesn't reach anyone.
//
// Reports written before this change are still under
// users/{uid}/errors and are only visible in the Firebase console.
//
// To add Sentry (or anything else) later, send from inside
// reportError() as well. Every call site already routes through here,
// so nothing else has to change.
// ═══════════════════════════════════════════════════════════════
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db, auth, IS_SANDBOX } from '../firebase.js';

// Same message repeating in a render loop shouldn't write a thousand
// documents. Remember what we've already sent this session.
const seen = new Set();
const MAX_PER_SESSION = 25;
let sentThisSession = 0;

// The identity of a BUG, as opposed to one occurrence of it. Stored
// on every report so the admin view can group a hundred occurrences
// of one crash into a single row instead of a hundred.
export function fingerprint(err, context) {
  return `${context}::${err?.name || ''}::${String(err?.message || err).slice(0, 200)}`;
}

// Trim a stack to something readable in the Firebase console without
// blowing past Firestore's 1MB document limit on a deep recursion.
function trimStack(stack) {
  if (!stack) return '';
  return String(stack).split('\n').slice(0, 30).join('\n').slice(0, 8000);
}

export async function reportError(err, context = 'unknown', extra = {}) {
  // Always log locally — this is what a developer sees in devtools.
  console.error(`[${context}]`, err, extra);

  try {
    if (sentThisSession >= MAX_PER_SESSION) return;
    const fp = fingerprint(err, context);
    if (seen.has(fp)) return;
    seen.add(fp);

    // See KNOWN GAP above — an unauthenticated crash has nowhere safe
    // to go.
    const user = auth.currentUser;
    if (!user) return;

    sentThisSession += 1;
    await addDoc(collection(db, 'errorReports'), {
      // `extra` goes FIRST so a call site can add context but can
      // never overwrite the fields below. userId in particular: the
      // rules require it to equal the caller's uid, so a call site
      // shadowing it wouldn't just be wrong, the write would be
      // rejected and the crash would go unrecorded.
      ...extra,
      userId: user.uid,
      userEmail: user.email || '',
      // Groups a hundred occurrences of one bug into one row.
      fingerprint: fp,
      context,
      name: err?.name || 'Error',
      message: String(err?.message || err).slice(0, 1000),
      stack: trimStack(err?.stack),
      // Where and on what — a bug that only happens on one browser is
      // impossible to chase without this.
      path: typeof window !== 'undefined' ? window.location.pathname : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 300) : '',
      online: typeof navigator !== 'undefined' ? navigator.onLine : null,
      env: IS_SANDBOX ? 'sandbox' : 'prod',
      appVersion: extra.appVersion || '',
      // Triage state. A crash marked handled comes back the moment it
      // happens again, because the NEW report starts unhandled.
      handledAt: null,
      createdAt: serverTimestamp(),
    });
  } catch (e) {
    // Reporting must never throw — if it does, the failure it was
    // reporting gets replaced by a confusing second one.
    console.error('[report-error] could not record the failure:', e);
  }
}

// Catch what React's error boundary can't see: failures in event
// handlers, timers, and rejected promises nobody awaited.
export function installGlobalErrorHandlers(appVersion = '') {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (event) => {
    reportError(event.error || new Error(event.message), 'window.onerror', { appVersion });
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason));
    reportError(reason, 'unhandledrejection', { appVersion });
  });
}
